import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { collection, query, getDocs, orderBy, where, limit, startAfter, getCountFromServer } from 'firebase/firestore';
import { db } from '../../firebase';
import { Timestamp } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface Article {
    id: string;
    title: string;
    content: string;
    category: string;
    status: string;
    publishDate: Timestamp;
}

interface CategoryConfig {
    categories: string[];
}

interface CategoryMap {
    [key: string]: string;
}

// Category mapping for filtering
const categoryMapping = {
    'news': 'news',
    'notice': 'announcements'
} as const;

// Type definitions
type CategoryKey = keyof typeof categoryMapping;

const PAGE_SIZE = 10; // 每页显示的新闻数量

const NewsListPage: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const [articles, setArticles] = useState<Article[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(t('news.categories.all'));
    
    // 修改路由监听逻辑、分类变化和分页
    useEffect(() => {
        setLoading(true);
        setArticles([]);

        // 立即获取数据
        const fetchData = async () => {
            try {
                const articlesRef = collection(db, 'articles');
                let baseConditions = [
                    where('status', '==', 'published'),
                ];

                // 根据选择的分类设置查询条件
                if (selectedCategory === t('news.categories.all')) {
                    baseConditions.push(where('category', 'in', ['news', 'announcements']));
                } else if (selectedCategory === t('news.categories.news')) {
                    baseConditions.push(where('category', '==', 'news'));
                } else if (selectedCategory === t('news.categories.notice')) {
                    baseConditions.push(where('category', '==', 'announcements'));
                }

                let q = query(
                    articlesRef,
                    ...baseConditions,
                    orderBy('publishDate', 'desc')
                );

                // 如果不是第一页，需要分页
                if (currentPage > 1) {
                    // 这里需要先获取前一页的数据来获取分页游标
                    const prevPageQuery = query(
                        articlesRef,
                        ...baseConditions,
                        orderBy('publishDate', 'desc'),
                        limit((currentPage - 1) * PAGE_SIZE)
                    );
                    const prevPageSnapshot = await getDocs(prevPageQuery);
                    const lastDoc = prevPageSnapshot.docs[prevPageSnapshot.docs.length - 1];

                    if (lastDoc) {
                        q = query(
                            articlesRef,
                            ...baseConditions,
                            orderBy('publishDate', 'desc'),
                            startAfter(lastDoc),
                            limit(PAGE_SIZE)
                        );
                    }
                } else {
                    q = query(
                        articlesRef,
                        ...baseConditions,
                        orderBy('publishDate', 'desc'),
                        limit(PAGE_SIZE)
                    );
                }

                const querySnapshot = await getDocs(q);
                const fetchedArticles = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as Article[];

                setArticles(fetchedArticles);
            } catch (error) {
                console.error('获取文章列表失败:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [location.pathname, selectedCategory, currentPage]);

    const pageTitle = t('news.title');
    const pageDescription = t('news.subtitle');

    // 获取总页数
    useEffect(() => {
        const fetchTotalCount = async () => {
            try {
                const articlesRef = collection(db, 'articles');
                let q;

                const baseConditions = [
                    where('status', '==', 'published')
                ];

                if (selectedCategory === t('news.categories.all')) {
                    baseConditions.push(where('category', 'in', ['news', 'announcements']));
                } else {
                    const categoryValue = categoryMapping[selectedCategory as CategoryKey];
                    if (categoryValue) {
                        baseConditions.push(where('category', '==', categoryValue));
                    }
                }

                q = query(articlesRef, ...baseConditions);
                const snapshot = await getCountFromServer(q);
                const total = snapshot.data().count;
                setTotalPages(Math.ceil(total / PAGE_SIZE));
            } catch (error) {
                console.error('获取文章总数失败:', error);
            }
        };

        fetchTotalCount();
    }, [selectedCategory]);

    const getTagColor = (category: string) => {
        return category === 'announcements' 
            ? 'bg-yellow-100 text-yellow-800' 
            : 'bg-blue-100 text-blue-800';
    };

    // 修改分页按钮渲染逻辑
    const renderPaginationButtons = () => {
        const buttons = [];
        const maxVisiblePages = 5; // 最多显示的页码数
        let startPage = 1;
        let endPage = Math.max(1, totalPages); // 确保至少显示1页

        if (totalPages > maxVisiblePages) {
            // 计算起始和结束页码
            if (currentPage <= Math.floor(maxVisiblePages / 2)) {
                endPage = maxVisiblePages;
            } else if (currentPage > totalPages - Math.floor(maxVisiblePages / 2)) {
                startPage = totalPages - maxVisiblePages + 1;
            } else {
                startPage = currentPage - Math.floor(maxVisiblePages / 2);
                endPage = currentPage + Math.floor(maxVisiblePages / 2);
            }
        }

        // 添加"上一页"按钮
        buttons.push(
            <button
                key="prev"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-md ${
                    currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
            >
                {t('news.pagination.previous')}
            </button>
        );

        // 如果起始页不是1，添加第一页和省略号
        if (startPage > 1) {
            buttons.push(
                <button
                    key={1}
                    onClick={() => setCurrentPage(1)}
                    className={`px-4 py-2 rounded-md ${
                        currentPage === 1 ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    1
                </button>
            );
            if (startPage > 2) {
                buttons.push(
                    <span key="dots1" className="px-4 py-2">
                        ...
                    </span>
                );
            }
        }

        // 添加页码按钮
        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    className={`px-4 py-2 rounded-md ${
                        currentPage === i ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    {i}
                </button>
            );
        }

        // 如果结束页不是最后一页，添加省略号和最后一页
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                buttons.push(
                    <span key="dots2" className="px-4 py-2">
                        ...
                    </span>
                );
            }
            buttons.push(
                <button
                    key={totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    className={`px-4 py-2 rounded-md ${
                        currentPage === totalPages ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    {totalPages}
                </button>
            );
        }

        // 添加"下一页"按钮
        buttons.push(
            <button
                key="next"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-md ${
                    currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
            >
                {t('news.pagination.next')}
            </button>
        );

        return buttons;
    };



    return (
        <div className="bg-white mt-32">
            {/* 页面标题区域 */}
            <div className="bg-gradient-to-r from-blue-800 to-indigo-900 py-16 text-center">
                <motion.h1 
                    className="text-4xl font-bold text-white mb-4"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {pageTitle}
                </motion.h1>
                <motion.p 
                    className="text-xl text-blue-100 max-w-3xl mx-auto px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    {pageDescription}
                </motion.p>
            </div>

            {/* 主要内容区域 */}
            <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                {/* 分类导航 */}
                <motion.div 
                    className="mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="flex justify-center space-x-8">
                        <button
                            key="all"
                            onClick={() => {
                                setSelectedCategory(t('news.categories.all'));
                                setCurrentPage(1);
                            }}
                            className={`py-2 px-6 rounded-full transition-all duration-300 ${
                                selectedCategory === t('news.categories.all')
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {t('news.categories.all')}
                        </button>
                        <button
                            key="news"
                            onClick={() => {
                                setSelectedCategory(t('news.categories.news'));
                                setCurrentPage(1);
                            }}
                            className={`py-2 px-6 rounded-full transition-all duration-300 ${
                                selectedCategory === t('news.categories.news')
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {t('news.categories.news')}
                        </button>
                        <button
                            key="notice"
                            onClick={() => {
                                setSelectedCategory(t('news.categories.notice'));
                                setCurrentPage(1);
                            }}
                            className={`py-2 px-6 rounded-full transition-all duration-300 ${
                                selectedCategory === t('news.categories.notice')
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {t('news.categories.notice')}
                        </button>
                    </div>
                </motion.div>

                {/* 文章列表 */}
                <motion.div
                    className="bg-white p-6 rounded-lg shadow-xl"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    {loading ? (
                        <div className="flex justify-center items-center min-h-[200px]">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                    ) : articles.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500">{t('news.empty')}</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {articles.map((article) => (
                                <motion.div 
                                    key={article.id}
                                    className="flex items-center justify-between py-4 hover:bg-gray-50 px-4 rounded-lg border-b border-gray-100 last:border-b-0"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="flex items-center space-x-4">
                                        <span className={`px-3 py-1 text-sm rounded-full ${
                                            article.category === 'announcements' 
                                                ? 'bg-yellow-100 text-yellow-800' 
                                                : 'bg-blue-100 text-blue-800'
                                        }`}>
                                            {article.category === 'announcements' 
                                                ? t('news.categories.notice') 
                                                : t('news.categories.news')}
                                        </span>
                                        <Link 
                                            to={`/article/${article.id}`}
                                            className="text-gray-900 hover:text-blue-600 transition-colors duration-300"
                                            onClick={() => {
                                                setTimeout(() => {
                                                    window.scrollTo(0, 0);
                                                }, 100);
                                            }}
                                        >
                                            {article.title}
                                        </Link>
                                    </div>
                                    <span className="text-gray-500 text-sm">
                                        {article.publishDate 
                                            ? article.publishDate.toDate().toLocaleDateString('zh-CN', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit'
                                            })
                                            : t('news.unpublished')}
                                    </span>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    {/* 分页 */}
                    <motion.nav 
                        className="mt-8 flex justify-center gap-2"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    >
                        {renderPaginationButtons()}
                    </motion.nav>
                </motion.div>
            </div>
        </div>
    );
};

export default NewsListPage; 